import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useAppSelector } from "../../store/hooks";
import { useIsWriter, isWriterRole } from "../blog/useIsWriter";

const LoadingShell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  background-color: #c9febe;
  color: #355;
`;

/**
 * admin or writer, read off the token claim. This is the cheap synchronous
 * check used to decide whether to *offer* an authoring link; anything that
 * gates access should use `useIsWriter`, which also asks the server.
 */
export const isWriter = isWriterRole;

type RequireWriterRouteProps = {
  children: React.ReactNode;
  /** Where a logged-in non-writer lands. The blog, unless a page says otherwise. */
  fallback?: string;
};

/**
 * Blocks the blog authoring pages until auth bootstrap finishes, then requires
 * login plus the `admin` or `writer` role. Same shape as `RequireAdminRoute`,
 * so the editor never renders (or fires its API calls) before the session is
 * known. The backend enforces the same pair independently.
 */
const RequireWriterRoute: React.FC<RequireWriterRouteProps> = ({ children, fallback = "/blog" }) => {
  const location = useLocation();
  const { token, bootstrapStatus } = useAppSelector((state) => state.auth);
  const { isWriter: allowed, isChecking } = useIsWriter();

  if (bootstrapStatus !== "ready" || isChecking) {
    return (
      <LoadingShell>
        <p>Loading session…</p>
      </LoadingShell>
    );
  }

  if (!token) {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?return=${returnTo}`} replace />;
  }

  if (!allowed) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
};

export default RequireWriterRoute;
